import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import type { NavItem, NavigationProps } from './navigation.types';
import './Navigation.styles.css';

// * Registered top navigation links
const NAV_ITEMS: readonly NavItem[] = [
  { name: 'Home', href: '#home' },
  { name: 'About', href: '#about' },
  { name: 'Projects', href: '#projects' },
  { name: 'Experience', href: '#experience' },
  { name: 'Achievements', href: '#achievements' },
  { name: 'Contact', href: '/contact' },
];

// * [COMPONENT] Fixed top navigation bar with active section indicator and responsive mobile drawer
export const Navigation: React.FC<NavigationProps> = ({ currentSection = 0, onNavClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isRecruiterActive, setIsRecruiterActive] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isContactPage = location.pathname === '/contact';

  React.useEffect(() => {
    const handleRecruiterChange = (e: Event): void => {
      const detail = (e as CustomEvent)?.detail;
      if (detail && typeof detail.active === 'boolean') {
        setIsRecruiterActive(detail.active);
      }
    };

    window.addEventListener('recruiter-mode-change', handleRecruiterChange);
    return () => window.removeEventListener('recruiter-mode-change', handleRecruiterChange);
  }, []);

  const handleClick = (e: React.MouseEvent, item: NavItem): void => {
    setIsMobileMenuOpen(false);

    if (item.href === '/contact') {
      e.preventDefault();
      navigate('/contact');
      return;
    }

    if (!isHomePage) {
      e.preventDefault();
      navigate(`/${item.href}`);
      return;
    }

    if (onNavClick) {
      onNavClick(item.href);
    }
  };

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  // * Show Experience and Achievements tabs ONLY when recruiter mode is active
  const displayedNavItems = NAV_ITEMS.filter((item) => {
    if (!isRecruiterActive && (item.name === 'Experience' || item.name === 'Achievements')) {
      return false;
    }
    return true;
  });

  const handleRecruiterToggle = (): void => {
    const next = !isRecruiterActive;
    setIsRecruiterActive(next);
    window.dispatchEvent(
      new CustomEvent('recruiter-mode-change', { detail: { active: next } })
    );
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        {/* * Brand logo pointing to top of document */}
        <div className="nav-logo">
          <Link
            to="/#home"
            className="logo-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="logo-text">YH</span>
          </Link>
        </div>

        {/* * Desktop navigation menu links */}
        <div className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {displayedNavItems.map((item, index) => {
            const isContactLink = item.href === '/contact';
            const isActive = isContactLink
              ? isContactPage
              : isHomePage && currentSection === index;

            return (
              <a
                key={index}
                href={isHomePage ? item.href : `/${item.href}`}
                className={`nav-link ${isActive ? 'active' : ''}`}
                onClick={(e) => handleClick(e, item)}
              >
                {item.name}
              </a>
            );
          })}
        </div>

        {/* * Universal Recruiter Mode Toggle Button */}
        <button
          type="button"
          className="nav-recruiter-pill"
          onClick={handleRecruiterToggle}
          aria-label="Toggle Recruiter Mode"
          aria-pressed={isRecruiterActive}
          title="Toggle Recruiter Mode"
        >
          <span
            className={`nav-status-dot ${isRecruiterActive ? 'green' : 'red'}`}
            aria-hidden="true"
          />
          <span className="nav-recruiter-text">Recruiter Mode</span>
        </button>

        {/* * Mobile hamburger menu trigger */}
        <button
          type="button"
          className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </nav>
  );
};

export default Navigation;

